<template>
	<v-container class="app-root pb-3" fluid>
		<!-- Assistance Panel -->
		<v-card v-if="targetAssistanceRoomId" class="panel assist-panel" outlined>
			<div class="assist-head d-flex align-center justify-space-between flex-wrap gap-2">
				<div class="d-inline-flex align-center gap-2">
					<v-icon size="16" color="primary">mdi-message-text-outline</v-icon>
					<span class="caption font-weight-bold">Assistance</span>
					<v-chip x-small :color="assistanceStatusColor" :outlined="!assistanceConnected">
						{{ assistanceStatusLabel }}
					</v-chip>
				</div>
				<div class="d-flex gap-1">
					<v-btn
						x-small
						color="primary"
						outlined
						:disabled="assistanceConnected || assistanceConnecting"
						@click="onAssistanceConnect">
						<v-icon left x-small>mdi-chat-processing</v-icon>
						Join
					</v-btn>
					<v-btn x-small text :disabled="!assistanceConnected && !assistanceConnecting" @click="disconnectAssistance">
						<v-icon left x-small>mdi-chat-remove</v-icon>
						Leave
					</v-btn>
				</div>
			</div>

			<div ref="assistanceLogEl" class="assist-log" role="log">
				<p v-if="!assistanceLog.length" class="caption text-medium-emphasis ma-0 pa-1">
					<template v-if="assistanceConnecting">Joining assistance room #{{ targetAssistanceRoomId }}…</template>
					<template v-else>Assistance room #{{ targetAssistanceRoomId }} — click Join to connect.</template>
				</p>
				<template v-for="entry in assistanceLog">
					<div
						v-if="entry.kind === 'chat'"
						:key="`chat-${entry.id}`"
						class="assist-bubble-row"
						:class="{ 'assist-bubble-row--out': entry.outgoing }">
						<div class="assist-bubble" :class="entry.outgoing ? 'assist-bubble--out' : 'assist-bubble--in'">
							<div class="assist-bubble-meta d-flex align-center gap-1">
								<span class="assist-bubble-author">{{ entry.outgoing ? 'You' : entry.author || 'Operator' }}</span>
								<span class="assist-bubble-sep">·</span>
								<span class="assist-bubble-time">{{
									new Date(entry.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
								}}</span>
							</div>
							<span class="assist-bubble-text">{{ entry.line }}</span>
						</div>
					</div>
					<div v-else :key="`sys-${entry.id}`" class="assist-system text-center">{{ entry.line }}</div>
				</template>
			</div>

			<div class="assist-compose d-flex align-center gap-2">
				<v-text-field
					v-model="assistanceDraft"
					dense
					hide-details
					outlined
					placeholder="Message to operator…"
					:disabled="!assistanceConnected"
					@keydown="assistanceOnKeydown" />
				<v-btn
					color="primary"
					small
					:disabled="!assistanceConnected || !assistanceDraft.trim()"
					@click="assistanceOnSend">
					Send
				</v-btn>
			</div>
		</v-card>

		<!-- Stream List -->
		<v-card class="panel">
			<div class="panel-head d-flex align-center justify-space-between flex-wrap gap-2">
				<h2 class="subtitle-1 font-weight-bold mb-0">{{ $t('live-stethoscopes') }}</h2>
				<div class="d-flex align-center gap-2">
					<v-btn text small color="primary" :disabled="!connected" @click="refreshNow">
						<v-icon left small>mdi-refresh</v-icon>
						{{ $t('refresh') }}
					</v-btn>
					<v-btn
						outlined
						small
						color="error"
						:disabled="!connected || filteredStreams.length === 0"
						:title="$t('destroy-all-title')"
						@click="onCleanAllClick">
						<v-icon left small>mdi-broom</v-icon>
						{{ $t('clean-all') }}
					</v-btn>
					<v-chip color="primary" small class="font-weight-bold">
						{{ filteredStreams.length }}
					</v-chip>
				</div>
			</div>

			<ul v-if="filteredStreams.length > 0" class="stream-list">
				<li
					v-for="s in filteredStreams"
					:key="s.id"
					class="stream-item"
					:class="{
						active: activeWatchId === s.id,
						'flash-new': getMark(s.id) === 'created',
						'flash-gone': getMark(s.id) === 'destroyed',
						'flash-meta': getMark(s.id) === 'metadata',
					}"
					@click="watchStream(s)">
					<div class="stream-row d-flex align-center justify-space-between">
						<div class="stream-info d-inline-flex align-center flex-wrap">
							<span v-if="s.metadata && s.metadata.patient && s.metadata.patient.name" class="bit">
								<span class="text-medium-emphasis mr-1">{{ $t('patient') }}:</span>
								<strong>{{ s.metadata.patient.name }}</strong>
							</span>
							<span v-if="s.metadata && s.metadata.operator && s.metadata.operator.name" class="bit">
								<span class="text-medium-emphasis mr-1">{{ $t('operator') }}:</span>
								<strong>{{ s.metadata.operator.name }}</strong>
							</span>
							<span v-if="s.metadata && s.metadata.hospital && s.metadata.hospital.name" class="bit">
								<span class="text-medium-emphasis mr-1">{{ $t('hospital-label') }}:</span>
								<strong>{{ s.metadata.hospital.name }}</strong>
							</span>
							<span
								v-if="
									!(s.metadata && s.metadata.patient && s.metadata.patient.name) &&
									!(s.metadata && s.metadata.operator && s.metadata.operator.name) &&
									!(s.metadata && s.metadata.hospital && s.metadata.hospital.name)
								"
								class="bit">
								{{ itemDescription(s) }}
							</span>
							<span class="stream-id">#{{ s.id }}</span>
						</div>

						<div class="d-flex align-center gap-2 flex-shrink-0 flex-wrap justify-end">
							<v-chip
								:color="s.enabled ? 'success' : undefined"
								:outlined="!s.enabled"
								small
								class="live-chip"
								:title="s.enabled ? $t('stream-enabled') : $t('stream-disabled')">
								<span class="live-dot" :class="{ on: s.enabled }" aria-hidden="true" />
								{{ s.enabled ? $t('live') : $t('off') }}
							</v-chip>

							<v-chip v-if="s.metadata && s.metadata.mode" color="primary" small :title="itemModeTitle(s)">
								<v-icon left small>{{ itemModeIcon(s) }}</v-icon>
								{{ itemModeLabel(s) }}
							</v-chip>

							<v-btn
								icon
								x-small
								color="error"
								outlined
								:title="$t('destroy-mountpoint-title', { id: s.id })"
								:aria-label="$t('destroy-mountpoint-title', { id: s.id })"
								@click.stop="onItemDestroyConfirm(s)">
								<v-icon small>mdi-trash-can-outline</v-icon>
							</v-btn>
						</div>
					</div>
				</li>
			</ul>

			<div v-else class="empty-placeholder text-medium-emphasis">
				<template v-if="connected">{{ $t('no-streams') }}</template>
				<template v-else>{{ $t('not-connected') }}</template>
			</div>
		</v-card>

		<!-- Player Panel -->
		<v-card v-if="showPlayer" class="panel stetho-panel pa-3 ma-0">
			<div class="stetho-topbar d-flex align-center justify-space-between flex-wrap gap-2 pb-2">
				<div class="stetho-title-block d-flex flex-column gap-1">
					<div class="d-inline-flex align-center gap-2">
						<v-icon size="22" color="primary">mdi-stethoscope</v-icon>
						<h2 class="stetho-title">{{ $t('digital-stethoscope') }}</h2>
					</div>
					<div class="stetho-sub d-flex flex-wrap text-medium-emphasis">
						<span v-for="(bit, idx) in playerSubtitleBits" :key="`sub-${idx}`" class="sub-bit">
							<template v-if="bit.key">
								<span class="text-medium-emphasis mr-1">{{ bit.key }}:</span>
								<strong>{{ bit.value }}</strong>
							</template>
							<template v-else>
								<span>{{ bit.value }}</span>
							</template>
						</span>
						<span v-if="activeStream" class="sub-id">#{{ activeStream.id }}</span>
					</div>
				</div>

				<div class="d-flex align-center gap-2 flex-wrap">
					<v-chip :color="playerIsLive ? 'success' : undefined" :outlined="!playerIsLive" small class="conn-chip">
						<span class="live-dot" :class="{ on: playerIsLive }" aria-hidden="true" />
						{{ $t(playerConnectionLabel) }}
					</v-chip>

					<v-chip color="primary" small :title="playerModeTitle">
						<v-icon left small>{{ playerModeIcon }}</v-icon>
						{{ $t(playerModeLabel) }}
					</v-chip>

					<v-btn color="#b37feb" tonal small @click="stopWatch">
						<v-icon left small>mdi-stop</v-icon>
						{{ $t('stop') }}
					</v-btn>
				</div>
			</div>

			<div class="stetho-row2 d-flex flex-wrap gap-2 align-center">
				<v-chip
					small
					:color="playerNoiseOn ? 'success' : undefined"
					:outlined="!playerNoiseOn"
					:title="$t('noise-reduction-status')">
					<v-icon left small>mdi-headphones</v-icon>
					<span class="chip-label">{{ $t('noise') }}</span>
					<span class="chip-val">{{ playerNoiseLabel }}</span>
				</v-chip>

				<v-chip
					small
					:color="bpmActive ? 'primary' : undefined"
					:outlined="!bpmActive"
					:class="{ 'bpm-stale': bpmStale, 'bpm-zero': bpmValue === 0 }"
					:title="bpmTitle">
					<v-icon left small>mdi-heart-pulse</v-icon>
					<span class="chip-val">{{ bpmDisplay }} BPM</span>
				</v-chip>

				<v-chip small outlined :title="$t('session-duration')">
					<v-icon left small>mdi-clock-outline</v-icon>
					<span class="chip-label">{{ $t('live-for') }}</span>
					<span class="chip-val">{{ playerLiveFor }}</span>
				</v-chip>

				<v-chip small outlined :title="$t('audio-codec')">
					<v-icon left small>mdi-waveform</v-icon>
					<span class="chip-val codec">{{ playerCodec }}</span>
				</v-chip>
			</div>

			<!-- Placement Guide -->
			<section class="placement-guide d-flex flex-column gap-2">
				<div class="pg-tabs d-inline-flex align-self-center">
					<button type="button" class="pg-tab" :class="{ active: pgSide === 'front' }" @click="pgSelectSide('front')">
						{{ $t('front') }}
					</button>
					<button type="button" class="pg-tab" :class="{ active: pgSide === 'back' }" @click="pgSelectSide('back')">
						{{ $t('back') }}
					</button>
				</div>

				<div ref="pgStageEl" class="pg-stage position-relative w-100">
					<img
						ref="pgImageEl"
						:src="pgImageSrc"
						:alt="$t('body-diagram')"
						class="pg-image"
						draggable="false"
						@load="onPgImageLoad" />
					<div
						class="pg-overlay position-absolute"
						:style="{
							left: `${pgOverlayBox.left}px`,
							top: `${pgOverlayBox.top}px`,
							width: `${pgOverlayBox.width}px`,
							height: `${pgOverlayBox.height}px`,
						}">
						<span
							v-for="p in pgPoints"
							:key="p.id"
							class="pg-point"
							:class="{ active: pgActivePoint && pgActivePoint.id === p.id }"
							:title="p.label"
							:style="pgDotStyle(p)" />
					</div>
				</div>

				<div class="d-flex flex-column align-center text-center gap-1">
					<div class="d-inline-flex align-center gap-2">
						<span
							class="pg-color-dot"
							:style="{
								background: pgActivePoint ? pgHeadlineColor : 'transparent',
								borderColor: pgActivePoint ? pgHeadlineColor : 'var(--pg-line)',
							}" />
						<span class="pg-label">{{ pgHeadlineLabel }}</span>
					</div>
					<p v-if="pgHeadlineInstruction" class="pg-instruction ma-0 text-medium-emphasis">
						{{ pgHeadlineInstruction }}
					</p>
				</div>
			</section>

			<!-- Waveform Canvas -->
			<div class="stetho-wave-container position-relative">
				<div class="stetho-wave-grid position-absolute" aria-hidden="true" />
				<canvas ref="canvasEl" class="stetho-wave w-100" width="640" height="120" />
			</div>

			<audio ref="audioEl" controls autoplay playsinline class="stetho-audio w-100" />
		</v-card>
	</v-container>
</template>

<script>
import { StreamingApp, cleanDescription } from '@/utils/streaming-app.js';
import { formatHeartRateAge, formatSessionAge, getAssistanceRoomId } from '@/utils/stream-metadata.js';
import { BACK_IMAGE_SIZE, BACK_POINTS, FRONT_IMAGE_SIZE, FRONT_POINTS } from '@/utils/placement-points.js';
import { AssistanceTextRoomClient } from '@/utils/textroom-client.js';
import { formatAssistanceLine } from '@/utils/assistance-messages.js';
import imgBack from '@/assets/pdfImages/placement_body_back.png';
import imgFront from '@/assets/pdfImages/placement_body_front.png';
import { mapState } from 'vuex';

const webrtc = process.env.VUE_APP_JANUS_SERVER_URL;
const HEART_RATE_STALE_MS = 10000;
const HEART_RATE_EXPIRED_MS = 30000;

export default {
	name: 'StethoscopeStreamingComponent',

	data () {
		return {
			pollMs: 2000,
			connected: false,
			streams: [],
			activeWatchId: null,
			activeStream: null,
			lifecycleMarks: new Map(),
			pgUserSide: null,
			pgOverlayBox: { left: 0, top: 0, width: 0, height: 0 },
			tick: 0,
			assistanceConnected: false,
			assistanceConnecting: false,
			assistanceRoomId: null,
			assistanceDraft: '',
			assistanceLog: [],
			assistanceNextId: 1,
		};
	},

	computed: {
		...mapState({
			authUser: (state) => state.authentication.userData,
			detailedUser: (state) => state.users.detailedUser,
		}),
		showPlayer () {
			return this.activeStream !== null;
		},
		filteredStreams () {
			let result = this.streams;
			const hospitalId = this.authUser.hospitalAssignedData.id;
			const patientId = this.detailedUser.userData.id;
			if (hospitalId !== null) {
				result = result.filter(
					(s) => Number(s.metadata && s.metadata.hospital && s.metadata.hospital.id) === hospitalId,
				);
			}
			if (patientId !== null) {
				result = result.filter((s) => Number(s.metadata && s.metadata.patient && s.metadata.patient.id) === patientId);
			}
			return result;
		},

		playerMd () {
			return this.activeStream ? this.activeStream.metadata : null;
		},
		playerIsBell () {
			return this.playerMd && this.playerMd.mode && this.playerMd.mode.toLowerCase() === 'bell';
		},
		playerModeIcon () {
			return this.playerIsBell ? 'mdi-bell-outline' : 'mdi-circle-outline';
		},
		playerModeLabel () {
			return this.playerIsBell ? 'bell' : 'diaphragm';
		},
		playerModeTitle () {
			return this.playerIsBell ? this.$t('mode-title-bell') : this.$t('mode-title-diaphragm');
		},
		playerNoiseOn () {
			return this.playerMd && this.playerMd.noiseReduction === true;
		},
		playerNoiseLabel () {
			return this.playerNoiseOn ? this.$t('on') : this.$t('off');
		},
		bpmAge () {
			// eslint-disable-next-line no-unused-expressions
			this.tick;
			const at = this.playerMd && this.playerMd.heartRateAt;
			if (!at || at <= 0) return null;
			return Math.max(0, Date.now() - at);
		},
		bpmExpired () {
			return this.bpmAge !== null && this.bpmAge > HEART_RATE_EXPIRED_MS;
		},
		bpmStale () {
			return this.bpmAge !== null && this.bpmAge > HEART_RATE_STALE_MS && !this.bpmExpired;
		},
		bpmValue () {
			const v = this.playerMd && this.playerMd.heartRateBpm;
			if (typeof v !== 'number' || v < 0) return null;
			if (this.bpmExpired) return null;
			return v;
		},
		bpmDisplay () {
			return this.bpmValue === null ? '--' : String(this.bpmValue);
		},
		bpmActive () {
			return this.bpmValue !== null && this.bpmValue > 0 && !this.bpmStale;
		},
		bpmTitle () {
			if (this.bpmValue === null) return this.$t('waiting-for-heart-rate');
			if (this.bpmValue === 0) return this.$t('no-beat');
			const ageText = this.bpmStale ? formatHeartRateAge(this.playerMd && this.playerMd.heartRateAt) : null;
			return ageText
				? this.$t('heart-rate-bpm-stale', { bpm: this.bpmValue, age: ageText })
				: this.$t('heart-rate-bpm', { bpm: this.bpmValue });
		},
		playerLiveFor () {
			// eslint-disable-next-line no-unused-expressions
			this.tick;
			return this.playerMd ? formatSessionAge(this.playerMd.sessionStartedAt) || '--' : '--';
		},
		playerCodec () {
			return (this.playerMd && this.playerMd.codec) || 'PCMU/8000';
		},
		playerSubtitleBits () {
			const bits = [];
			if (this.playerMd && this.playerMd.patient && this.playerMd.patient.name) {
				bits.push({ key: this.$t('patient'), value: this.playerMd.patient.name });
			}
			if (this.playerMd && this.playerMd.operator && this.playerMd.operator.name) {
				bits.push({ key: this.$t('operator'), value: this.playerMd.operator.name });
			}
			if (this.playerMd && this.playerMd.hospital && this.playerMd.hospital.name) {
				bits.push({ key: this.$t('hospital-label'), value: this.playerMd.hospital.name });
			}
			if (bits.length === 0 && this.activeStream) bits.push({ value: cleanDescription(this.activeStream.description) });
			return bits;
		},
		playerIsLive () {
			return this.activeWatchId !== null;
		},
		playerConnectionLabel () {
			return this.playerIsLive ? 'live' : 'disconnected';
		},

		pgSide () {
			if (this.pgUserSide) return this.pgUserSide;
			const md =
				this.activeStream &&
				this.activeStream.metadata &&
				this.activeStream.metadata.placement &&
				this.activeStream.metadata.placement.side;
			if (md === 'back' || md === 'front') return md;
			return 'front';
		},
		pgImageSrc () {
			return this.pgSide === 'back' ? imgBack : imgFront;
		},
		pgBodySize () {
			return this.pgSide === 'back' ? BACK_IMAGE_SIZE : FRONT_IMAGE_SIZE;
		},
		pgPoints () {
			return this.pgSide === 'back' ? BACK_POINTS : FRONT_POINTS;
		},
		pgActivePoint () {
			const id =
				this.activeStream &&
				this.activeStream.metadata &&
				this.activeStream.metadata.placement &&
				this.activeStream.metadata.placement.id;
			if (!id) return null;
			return FRONT_POINTS.find((p) => p.id === id) || BACK_POINTS.find((p) => p.id === id) || null;
		},
		pgHeadlineLabel () {
			if (this.pgActivePoint) return this.pgActivePoint.label;
			if (
				this.activeStream &&
				this.activeStream.metadata &&
				this.activeStream.metadata.placement &&
				this.activeStream.metadata.placement.label
			) {
				return this.activeStream.metadata.placement.label;
			}
			return this.$t('waiting-for-placement');
		},
		pgHeadlineInstruction () {
			return (this.pgActivePoint && this.pgActivePoint.instruction) || '';
		},
		pgHeadlineColor () {
			return (this.pgActivePoint && this.pgActivePoint.color) || 'transparent';
		},

		assistanceRoomIdFromStream () {
			return getAssistanceRoomId(this.activeStream && this.activeStream.metadata);
		},
		targetAssistanceRoomId () {
			return this.assistanceRoomIdFromStream || null;
		},
		assistanceStatusColor () {
			if (this.assistanceConnected) return 'success';
			return this.targetAssistanceRoomId ? 'warning' : undefined;
		},
		assistanceStatusLabel () {
			if (this.assistanceConnected) return `room #${this.assistanceRoomId}`;
			if (this.assistanceConnecting && this.targetAssistanceRoomId) return `joining #${this.targetAssistanceRoomId}…`;
			return this.targetAssistanceRoomId ? `pending #${this.targetAssistanceRoomId}` : 'waiting for stream';
		},
		assistanceDisplayName () {
			const u = this.authUser;
			return [u.firstName, u.lastName].filter(Boolean).join(' ') || 'Clinician';
		},
	},

	created () {
		this.pgObserver = null;
		this.tickHandle = null;
		this.assistanceClient = null;
		this.app = new StreamingApp(
			(next) => {
				this.streams = next;
				if (this.activeWatchId != null) {
					const live = next.find((s) => s.id === this.activeWatchId);
					if (live) this.activeStream = live;
				}
			},
			(event, stream) => {
				const next = new Map(this.lifecycleMarks);
				next.set(stream.id, event);
				this.lifecycleMarks = next;

				if (event === 'destroyed' && this.activeWatchId === stream.id) {
					this.activeWatchId = null;
					this.activeStream = null;
				}
				if (event === 'metadata' && this.activeWatchId === stream.id) {
					this.activeStream = { ...stream };
				}

				setTimeout(() => {
					const cur = new Map(this.lifecycleMarks);
					if (cur.get(stream.id) === event) {
						cur.delete(stream.id);
						this.lifecycleMarks = cur;
					}
				}, 1200);
			},
			(isConnected) => {
				this.connected = isConnected;
				if (!isConnected) {
					this.activeWatchId = null;
					this.activeStream = null;
				}
			},
			() => {},
		);
	},

	mounted () {
		this.triggerConnect();
	},

	beforeDestroy () {
		this.stopTick();
		this.teardownPgObserver();
		this.disconnectAssistance();
		try {
			this.app.disconnect();
		} catch {
			/* ignore */
		}
	},

	methods: {
		triggerConnect () {
			if (!webrtc) return;
			this.app.connect(webrtc, this.pollMs);
		},

		watchStream (stream) {
			this.activeWatchId = stream.id;
			this.activeStream = stream;
			this.app.prepareAudio();
			this.app.watchStream(stream);
		},

		stopWatch () {
			this.app.stopWatch();
			this.activeWatchId = null;
			this.activeStream = null;
		},

		destroyStream (id) {
			this.app.destroyStream(id);
		},

		destroyAll (ids) {
			this.app.destroyAll(ids);
		},

		refreshNow () {
			this.app.refreshNow();
		},

		onCleanAllClick () {
			if (this.filteredStreams.length === 0) return;
			const ids = this.filteredStreams.map((s) => s.id);
			const confirmed = window.confirm(
				`${this.$t('destroy-all-confirm', { count: ids.length })}\n\n` +
					ids.map((id) => `#${id}`).join(', ') +
					`\n\n${this.$t('destroy-all-warning')}`,
			);
			if (!confirmed) return;
			this.destroyAll(ids);
		},

		onItemDestroyConfirm (stream) {
			if (!window.confirm(`${this.$t('destroy-confirm', { id: stream.id })}\n\n${this.$t('destroy-warning')}`)) {
				return;
			}
			this.destroyStream(stream.id);
		},

		itemDescription (stream) {
			return cleanDescription(stream.description);
		},

		itemIsBell (stream) {
			return stream.metadata && stream.metadata.mode && stream.metadata.mode.toLowerCase() === 'bell';
		},

		itemModeLabel (stream) {
			return this.itemIsBell(stream) ? this.$t('bell') : this.$t('diaphragm');
		},

		itemModeIcon (stream) {
			return this.itemIsBell(stream) ? 'mdi-bell-outline' : 'mdi-circle-outline';
		},

		itemModeTitle (stream) {
			return this.itemIsBell(stream) ? this.$t('mode-title-bell') : this.$t('mode-title-diaphragm');
		},

		getMark (id) {
			return this.lifecycleMarks.get(id) || null;
		},

		pgSelectSide (s) {
			this.pgUserSide = s;
		},

		pgLayout () {
			const img = this.$refs.pgImageEl;
			if (!img) return;
			const rect = img.getBoundingClientRect();
			if (rect.width === 0 || rect.height === 0) return;
			const bw = this.pgBodySize.w;
			const bh = this.pgBodySize.h;
			const scale = Math.min(rect.width / bw, rect.height / bh);
			const renderedW = bw * scale;
			const renderedH = bh * scale;
			this.pgOverlayBox = {
				left: (rect.width - renderedW) / 2,
				top: (rect.height - renderedH) / 2,
				width: renderedW,
				height: renderedH,
			};
		},

		onPgImageLoad () {
			this.pgLayout();
		},

		setupPgObserver () {
			this.teardownPgObserver();
			if (this.$refs.pgStageEl) {
				this.pgObserver = new ResizeObserver(() => this.pgLayout());
				this.pgObserver.observe(this.$refs.pgStageEl);
			}
		},

		teardownPgObserver () {
			if (this.pgObserver) {
				this.pgObserver.disconnect();
				this.pgObserver = null;
			}
		},

		pgDotStyle (p) {
			return {
				left: `${(p.bitmapX / this.pgBodySize.w) * 100}%`,
				top: `${(p.bitmapY / this.pgBodySize.h) * 100}%`,
				'--pg-color': p.color,
			};
		},

		startTick () {
			if (this.tickHandle) return;
			this.tickHandle = setInterval(() => {
				this.tick++;
			}, 1000);
		},

		stopTick () {
			if (this.tickHandle) {
				clearInterval(this.tickHandle);
				this.tickHandle = null;
			}
		},

		appendAssistanceLog (line, kind = 'system', outgoing = false, author = undefined) {
			this.assistanceLog = [
				...this.assistanceLog,
				{ id: this.assistanceNextId++, at: Date.now(), line, kind, outgoing, author },
			].slice(-200);
		},

		connectAssistance (roomId) {
			this.disconnectAssistance();
			if (!roomId || roomId <= 0 || !webrtc) return;
			this.assistanceConnecting = true;
			this.assistanceClient = new AssistanceTextRoomClient(
				(isUp) => {
					this.assistanceConnecting = false;
					this.assistanceConnected = isUp;
					this.assistanceRoomId = isUp ? roomId : null;
				},
				(msg) => {
					if (msg.type === 'chat') {
						const text = String((msg.payload && msg.payload.text) || '').trim();
						if (!text) return;
						this.appendAssistanceLog(text, 'chat', false, (msg.from && msg.from.name) || (msg.from && msg.from.role));
						return;
					}
					if (msg.type === 'device' && msg.payload && msg.payload.event === 'heartRate') return;
					this.appendAssistanceLog(formatAssistanceLine(msg), msg.type, false);
				},
			);
			this.assistanceClient.connect(webrtc, roomId, this.assistanceDisplayName, { role: 'clinician' });
		},

		disconnectAssistance () {
			if (this.assistanceClient) {
				this.assistanceClient.disconnect();
				this.assistanceClient = null;
			}
			this.assistanceConnecting = false;
			this.assistanceConnected = false;
			this.assistanceRoomId = null;
		},

		syncAssistanceFromActiveStream () {
			const room = this.targetAssistanceRoomId;
			if (!room) return;
			if (this.assistanceConnecting) return;
			if (this.assistanceConnected && this.assistanceRoomId === room) return;
			this.connectAssistance(room);
		},

		onAssistanceConnect () {
			if (this.targetAssistanceRoomId) this.connectAssistance(this.targetAssistanceRoomId);
		},

		assistanceOnSend () {
			const text = this.assistanceDraft.trim();
			if (!text || !this.assistanceClient) return;
			this.assistanceClient.sendChat(text);
			this.appendAssistanceLog(text, 'chat', true);
			this.assistanceDraft = '';
		},

		assistanceOnKeydown (e) {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				this.assistanceOnSend();
			}
		},
	},

	watch: {
		showPlayer (val) {
			if (val) {
				this.$nextTick(() => {
					this.setupPgObserver();
					if (this.$refs.audioEl && this.$refs.canvasEl) {
						this.app.setPlayerElements(this.$refs.audioEl, this.$refs.canvasEl);
					}
					this.startTick();
				});
			} else {
				this.teardownPgObserver();
				this.stopTick();
			}
		},
		pgSide () {
			requestAnimationFrame(() => this.pgLayout());
		},
		assistanceRoomIdFromStream (room, prev) {
			if (room === prev) return;
			if (room) {
				this.syncAssistanceFromActiveStream();
				return;
			}
			if (prev) this.disconnectAssistance();
		},
		activeWatchId (id) {
			if (id == null) this.disconnectAssistance();
		},
		assistanceLog () {
			this.$nextTick(() => {
				const el = this.$refs.assistanceLogEl;
				if (el) el.scrollTop = el.scrollHeight;
			});
		},
	},
};
</script>

<style scoped>
/* Layout utilities not in Vuetify 2 */
.gap-1 {
	gap: 4px;
}
.gap-2 {
	gap: 8px;
}
.w-100 {
	width: 100%;
}
.position-relative {
	position: relative;
}
.position-absolute {
	position: absolute;
}
.flex-shrink-0 {
	flex-shrink: 0;
}
.text-medium-emphasis {
	color: rgba(0, 0, 0, 0.6);
}

.app-root {
	padding: 12px 12px 24px;
	max-width: 880px;
}

.panel {
	padding: 10px 12px;
	margin-bottom: 10px;
}

.panel-head {
	margin-bottom: 6px;
}

.stream-list {
	list-style: none;
	margin: 0;
	padding: 0;
}

.empty-placeholder {
	padding: 12px 4px;
	font-size: 13px;
}

.stream-item {
	display: block;
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 8px;
	margin-bottom: 6px;
	cursor: pointer;
	transition: background 0.15s, border-color 0.15s;
	list-style: none;
	background: #fff;
}

.stream-row {
	gap: 10px;
	padding: 8px 10px;
}

.stream-info {
	gap: 4px 14px;
	font-size: 13px;
	min-width: 0;
	flex: 1;
}

.stream-id {
	font-weight: 600;
	color: #6b6f76;
	opacity: 0.65;
	font-variant-numeric: tabular-nums;
}

.stream-item:hover {
	background: #fafbfc;
	border-color: #c8ccd4;
}

.stream-item.active {
	border-color: #a51717;
	background: #fff5f5;
}

.stream-item.flash-new {
	animation: flashNew 1.2s ease;
}

.stream-item.flash-meta {
	animation: flashMeta 1.2s ease;
}

.stream-item.flash-gone {
	opacity: 0.5;
}

@keyframes flashNew {
	0% {
		background: #e8f5e9;
	}
	100% {
		background: transparent;
	}
}

@keyframes flashMeta {
	0% {
		background: #fff8e1;
	}
	100% {
		background: transparent;
	}
}

.live-chip .live-dot {
	display: inline-block;
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: #9aa0a6;
	margin-right: 6px;
	box-shadow: 0 0 0 0 rgba(46, 125, 50, 0);
}

.live-chip .live-dot.on {
	background: #2e7d32;
	animation: connDotPulse 1.6s ease-in-out infinite;
}

.panel.stetho-panel {
	background: linear-gradient(180deg, #fff 0%, #fdf6f6 100%);
}

.stetho-topbar {
	border-bottom: 1px solid #e2e5ea;
	margin-bottom: 10px;
}

.stetho-title-block {
	min-width: 0;
	flex: 1;
}

.stetho-title {
	margin: 0;
	font-size: 15px;
	font-weight: 700;
	color: #a51717;
	letter-spacing: 0.01em;
}

.stetho-sub {
	font-size: 11px;
	gap: 2px 10px;
}

.stetho-sub strong {
	color: #1f2024;
	font-weight: 600;
}

.sub-id {
	opacity: 0.6;
}

.conn-chip .live-dot {
	display: inline-block;
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: #9aa0a6;
	margin-right: 6px;
}

.conn-chip .live-dot.on {
	background: #2e7d32;
	animation: connDotPulse 1.6s ease-in-out infinite;
}

@keyframes connDotPulse {
	0%,
	100% {
		box-shadow: 0 0 0 0 rgba(46, 125, 50, 0.45);
	}
	60% {
		box-shadow: 0 0 0 6px rgba(46, 125, 50, 0);
	}
}

.stetho-row2 {
	margin-bottom: 10px;
}

.chip-label {
	font-size: 9px;
	font-weight: 700;
	letter-spacing: 0.08em;
	color: #6b6f76;
	text-transform: uppercase;
	margin-right: 4px;
}

.chip-val {
	font-weight: 700;
	font-variant-numeric: tabular-nums;
	font-size: 12px;
}

.chip-val.codec {
	font-size: 11px;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: #6b6f76;
}

.bpm-stale {
	opacity: 0.7;
}

.bpm-zero {
	opacity: 0.85;
}
.bpm-zero ::v-deep .v-icon {
	color: #6b6f76 !important;
}
.bpm-zero .chip-val {
	color: #6b6f76;
}

.stetho-audio {
	height: 28px;
	margin-top: 6px;
}

.placement-guide {
	--pg-line: #e2e5ea;
}

.pg-tabs {
	background: #f1eded;
	border-radius: 999px;
	padding: 2px;
	gap: 2px;
}

.pg-tab {
	border: 0;
	background: transparent;
	font: inherit;
	font-size: 11px;
	font-weight: 600;
	color: #6b6f76;
	padding: 4px 14px;
	border-radius: 999px;
	cursor: pointer;
	letter-spacing: 0.02em;
}

.pg-tab.active {
	background: #a51717;
	color: #fff;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

.pg-stage {
	max-width: 260px;
	margin: 0 auto;
	aspect-ratio: 634 / 640;
	background: #f7f3f3;
	border-radius: 12px;
	overflow: hidden;
}

.pg-image {
	width: 100%;
	height: 100%;
	object-fit: contain;
	display: block;
	user-select: none;
	-webkit-user-drag: none;
}

.pg-overlay {
	pointer-events: none;
}

.pg-point {
	position: absolute;
	width: 9px;
	height: 9px;
	margin: -4.5px 0 0 -4.5px;
	border-radius: 50%;
	background: var(--pg-color, #a51717);
	opacity: 0.45;
	border: 1.5px solid #fff;
	box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
	transition: opacity 0.2s, transform 0.2s;
}

.pg-point.active {
	width: 14px;
	height: 14px;
	margin: -7px 0 0 -7px;
	opacity: 1;
	animation: pgPointPulse 1.4s ease-in-out infinite;
}

@keyframes pgPointPulse {
	0%,
	100% {
		box-shadow: 0 0 0 0 var(--pg-color, #a51717), 0 0 0 2px #fff inset;
		transform: scale(1);
	}
	60% {
		box-shadow: 0 0 0 10px rgba(255, 255, 255, 0), 0 0 0 2px #fff inset;
		transform: scale(1.08);
	}
}

.pg-color-dot {
	width: 12px;
	height: 12px;
	border-radius: 50%;
	border: 2px solid var(--pg-line);
	background: transparent;
}

.pg-label {
	font-size: 13px;
	font-weight: 700;
	color: #1f2024;
}

.pg-instruction {
	max-width: 340px;
	font-size: 11px;
	line-height: 1.35;
}

.stetho-wave-container {
	background: #0d0e10;
	border-radius: 8px;
	overflow: hidden;
	margin-bottom: 6px;
	height: 90px;
}

.stetho-wave-grid {
	inset: 0;
	pointer-events: none;
	background-image: linear-gradient(rgba(165, 23, 23, 0.18) 1px, transparent 1px),
		linear-gradient(90deg, rgba(165, 23, 23, 0.18) 1px, transparent 1px),
		linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
		linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
	background-size: 80px 40px, 80px 40px, 16px 8px, 16px 8px;
	background-position: 0 50%, 0 50%, 0 50%, 0 50%;
}

.stetho-wave {
	display: block;
	height: 100%;
	background: transparent;
}

.assist-panel {
	border-color: rgba(165, 23, 23, 0.15);
}

.assist-head {
	padding: 7px 12px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.assist-log {
	max-height: 150px;
	overflow-y: auto;
	padding: 6px 12px;
	font-size: 12px;
	line-height: 1.45;
	background: #fafafa;
}

.assist-bubble-row {
	display: flex;
	margin: 2px 0;
}

.assist-bubble-row--out {
	justify-content: flex-end;
}

.assist-bubble {
	max-width: 75%;
	padding: 5px 9px 4px;
	border-radius: 12px;
	font-size: 12px;
	line-height: 1.35;
	display: inline-flex;
	flex-direction: column;
	word-break: break-word;
}

.assist-bubble--in {
	background: #f1f3f5;
	border: 1px solid #e1e4e8;
	color: #1f2328;
	border-bottom-left-radius: 3px;
}

.assist-bubble--out {
	background: #a51717;
	color: #fff;
	border-bottom-right-radius: 3px;
}

.assist-bubble-meta {
	font-size: 10px;
	opacity: 0.7;
	margin-bottom: 2px;
}

.assist-bubble-author {
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
}

.assist-bubble-sep {
	opacity: 0.6;
}

.assist-bubble-time {
	font-variant-numeric: tabular-nums;
}

.assist-bubble-text {
	white-space: pre-wrap;
}

.assist-system {
	color: #6b6f76;
	font-size: 11px;
	font-style: italic;
	margin: 3px 0;
}

.assist-compose {
	padding: 7px 12px 9px;
}

.assist-compose ::v-deep .v-text-field input {
	font-size: 13px;
}
</style>
